// Local notifications for the native app (iOS first). The device schedules them itself from its own
// data, so no server, push token or personal data is involved. In a browser there is nothing to
// schedule with, so reminders are native-only; a stand-in (localStorage snapcook_fake_notify=1)
// records what would be scheduled, for tests and previews.

import { Capacitor } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import type { Reminder } from '../domain/notifications';

export type Permission = 'granted' | 'denied' | 'prompt';

export interface Notifier {
  permission(): Promise<Permission>;
  /** Shows the system prompt when it hasn't been answered yet. */
  request(): Promise<Permission>;
  /** Replaces every scheduled reminder (cooking timers are left alone). */
  setReminders(reminders: Reminder[]): Promise<void>;
  scheduleTimer(id: number, at: Date, title: string, body: string, route: string): Promise<void>;
  cancelTimer(id: number): Promise<void>;
  /** Called with the route of a tapped notification. */
  onOpen(handler: (route: string) => void): void;
}

/** Reminder ids are large (kind and date); timer ids stay below this. */
export const TIMER_ID_LIMIT = 100_000_000;

const toPermission = (p: string): Permission => (p === 'granted' ? 'granted' : p === 'denied' ? 'denied' : 'prompt');

const native: Notifier = {
  async permission() { return toPermission((await LocalNotifications.checkPermissions()).display); },
  async request() { return toPermission((await LocalNotifications.requestPermissions()).display); },
  async setReminders(reminders) {
    const pending = (await LocalNotifications.getPending()).notifications.filter((n) => n.id >= TIMER_ID_LIMIT);
    if (pending.length) await LocalNotifications.cancel({ notifications: pending.map((n) => ({ id: n.id })) });
    if (!reminders.length) return;
    await LocalNotifications.schedule({
      notifications: reminders.map((r) => ({ id: r.id, title: r.title, body: r.body, schedule: { at: r.at, allowWhileIdle: true }, extra: { route: r.route } })),
    });
  },
  async scheduleTimer(id, at, title, body, route) {
    await LocalNotifications.schedule({ notifications: [{ id, title, body, schedule: { at, allowWhileIdle: true }, extra: { route } }] });
  },
  async cancelTimer(id) { await LocalNotifications.cancel({ notifications: [{ id }] }); },
  onOpen(handler) {
    void LocalNotifications.addListener('localNotificationActionPerformed', (e) => {
      const route = (e.notification.extra as { route?: string } | undefined)?.route;
      if (route?.startsWith('/')) handler(route);
    });
  },
};

const KEY = { permission: 'snapcook_fake_notify_permission', scheduled: 'snapcook_fake_notify_scheduled' };
const ls = {
  get: (k: string) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* ignore */ } },
};
type Fake = { id: number; title: string; body: string; at: string; route: string };
const readFake = (): Fake[] => JSON.parse(ls.get(KEY.scheduled) ?? '[]');

/** Test and preview stand-in: records schedules in localStorage. */
const fake: Notifier = {
  async permission() { return (ls.get(KEY.permission) as Permission | null) ?? 'prompt'; },
  async request() {
    const current = await this.permission();
    if (current !== 'prompt') return current;
    const answer = (ls.get('snapcook_fake_notify_answer') as Permission | null) ?? 'granted';
    ls.set(KEY.permission, answer);
    return answer;
  },
  async setReminders(reminders) {
    const timers = readFake().filter((n) => n.id < TIMER_ID_LIMIT);
    ls.set(KEY.scheduled, JSON.stringify([...timers, ...reminders.map((r) => ({ id: r.id, title: r.title, body: r.body, at: r.at.toISOString(), route: r.route }))]));
  },
  async scheduleTimer(id, at, title, body, route) {
    ls.set(KEY.scheduled, JSON.stringify([...readFake().filter((n) => n.id !== id), { id, title, body, at: at.toISOString(), route }]));
  },
  async cancelTimer(id) { ls.set(KEY.scheduled, JSON.stringify(readFake().filter((n) => n.id !== id))); },
  onOpen() { /* nothing to tap in tests */ },
};

/** The notifier for this platform, or null where reminders aren't available (a plain browser). */
export function notifier(): Notifier | null {
  if (Capacitor.isNativePlatform()) return native;
  return ls.get('snapcook_fake_notify') === '1' ? fake : null;
}
